import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Tooltip, Button, Alert, Chip, Divider, MenuItem,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, Grid, CircularProgress
} from '@mui/material';
import {
  Add, CheckCircle, Cancel, ThumbDown, ArrowBack, Assignment, Send, Undo, Edit
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import AttachmentsPanel from './AttachmentsPanel';
import { useLeaveTypes } from '../hooks/useLeaveTypes';
import { locale } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  'Open': 'default', 'Pending Approval': 'warning',
  'Approved': 'success', 'Rejected': 'error', 'Cancelled': 'default', 'Recalled': 'secondary',
};

const TODAY = new Date().toISOString().split('T')[0];

// Calendar-day count used for recall preview (inclusive)
const calDays = (a, b) => {
  if (!a || !b) return 0;
  const s = new Date(a); s.setHours(0, 0, 0, 0);
  const e = new Date(b); e.setHours(0, 0, 0, 0);
  if (e < s) return 0;
  return Math.round((e - s) / 86400000) + 1;
};

const workingDays = (start, end) => {
  if (!start || !end) return 0;
  let count = 0;
  const d = new Date(start); d.setHours(0, 0, 0, 0);
  const e = new Date(end);   e.setHours(0, 0, 0, 0);
  if (e < d) return 0;
  while (d <= e) { if (d.getDay() !== 0 && d.getDay() !== 6) count++; d.setDate(d.getDate() + 1); }
  return count;
};

const addCalDays = (dateStr, days) => {
  if (!dateStr || !days) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days - 1);
  return d.toISOString().split('T')[0];
};

const nextWorkDay = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const toDateStr = (v) => {
  if (!v) return '';
  try { return new Date(v).toISOString().split('T')[0]; } catch { return ''; }
};

const previewRecall = (app, recallDateStr) => {
  if (!recallDateStr || !app) return 0;
  const recall = new Date(recallDateStr); recall.setHours(0, 0, 0, 0);
  const start  = new Date(app.startDate); start.setHours(0, 0, 0, 0);
  const end    = new Date(app.endDate);   end.setHours(0, 0, 0, 0);
  if (recall <= start) return app.leaveDays;
  if (recall > end)    return 0;
  const dayBefore = new Date(recall); dayBefore.setDate(dayBefore.getDate() - 1);
  const total = calDays(start, end);
  const taken = calDays(start, dayBefore);
  return Math.round(app.leaveDays * (total - taken) / total);
};

const LeaveApplicationList = () => {
  const navigate = useNavigate();
  const { leaveTypes } = useLeaveTypes();

  const [apps,        setApps]        = useState([]);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [loading,     setLoading]     = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterYear,   setFilterYear]   = useState(new Date().getFullYear().toString());

  // Reject dialog
  const [rejectDlg,   setRejectDlg]   = useState(null);
  const [rejectReason,setRejectReason] = useState('');

  // Recall dialog
  const [recallDlg,   setRecallDlg]   = useState(null);   // the full app object
  const [recallDate,  setRecallDate]   = useState(TODAY);
  const [recalling,   setRecalling]    = useState(false);

  // Edit dialog
  const [employees,   setEmployees]   = useState([]);
  const [editDlg,     setEditDlg]     = useState(null);   // the full app object being edited
  const [editForm,    setEditForm]     = useState({});
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: !!editDlg, value: editForm, onClose: () => setEditDlg(null) });
  const [editLT,      setEditLT]      = useState(null);   // resolved leave type
  const [editBal,     setEditBal]     = useState(null);   // current balance
  const [editSaving,  setEditSaving]  = useState(false);
  const [editErr,     setEditErr]     = useState('');

  // Load employees list once (needed for reliever dropdown)
  useEffect(() => {
    axios.get(`${API}/api/employees`, { headers: hdrs() })
      .then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  // When edit dialog opens, resolve the leave type and balance
  useEffect(() => {
    if (!editDlg) { setEditLT(null); setEditBal(null); return; }
    const lt = leaveTypes.find(t => t.code === editForm.leaveTypeCode);
    setEditLT(lt || null);
    if (lt && lt.daysPerYear !== null && editForm.startDate) {
      const year = new Date(editForm.startDate).getFullYear();
      axios.get(`${API}/api/leave/balance/${editDlg.employeeNo}?year=${year}`, { headers: hdrs() })
        .then(r => {
          const b = (r.data || []).find(x => x.leaveTypeCode === editForm.leaveTypeCode);
          // Add back the application's own days only if the leave type hasn't changed,
          // so balance shows "available if this application didn't exist"
          const ownDays = editForm.leaveTypeCode === editDlg.leaveTypeCode ? editDlg.leaveDays : 0;
          setEditBal((b ? b.balance : 0) + ownDays);
        }).catch(() => setEditBal(null));
    } else {
      setEditBal(null);
    }
  }, [editDlg, editForm.leaveTypeCode, editForm.startDate, leaveTypes]); // eslint-disable-line

  const openEdit = (app) => {
    setEditForm({
      leaveTypeCode: app.leaveTypeCode,
      startDate:     toDateStr(app.startDate),
      endDate:       toDateStr(app.endDate),
      relieverNo:    app.relieverNo || '',
      reason:        app.reason || '',
    });
    setEditDlg(app);
    setEditErr('');
  };

  const handleEditField = (e) => {
    const { name, value } = e.target;
    setEditForm(f => {
      const next = { ...f, [name]: value };
      const ltCode  = name === 'leaveTypeCode' ? value : f.leaveTypeCode;
      const lt      = leaveTypes.find(t => t.code === ltCode);
      const maxDays = lt?.maxDaysPerApplication || 0;
      if (name === 'startDate' && maxDays > 0 && value) {
        next.endDate = addCalDays(value, maxDays);
      }
      if (name === 'leaveTypeCode' && maxDays > 0 && f.startDate) {
        next.endDate = addCalDays(f.startDate, maxDays);
      }
      return next;
    });
    setEditErr('');
  };

  const handleEditSave = async () => {
    const { leaveTypeCode, relieverNo } = editForm;
    const lt = leaveTypes.find(t => t.code === leaveTypeCode);
    // Client-side reliever check
    if (lt?.requiresReliever && !relieverNo) {
      setEditErr(`${lt.name} requires a reliever — please assign one before saving`);
      return;
    }
    setEditSaving(true); setEditErr('');
    try {
      const updated = await axios.put(
        `${API}/api/leave/applications/${editDlg._id}`,
        editForm,
        { headers: hdrs() }
      );
      setApps(prev => prev.map(a => a._id === editDlg._id ? updated.data : a));
      setSuccess('Application updated');
      setTimeout(() => setSuccess(''), 3000);
      setEditDlg(null);
    } catch (err) {
      setEditErr(err.response?.data?.error || 'Save failed');
    } finally { setEditSaving(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType)   params.set('leaveTypeCode', filterType);
      if (filterYear)   params.set('year', filterYear);
      const { data } = await axios.get(`${API}/api/leave/applications?${params}`, { headers: hdrs() });
      setApps(data);
    } catch { setError('Failed to load applications'); }
    finally { setLoading(false); }
  }, [filterStatus, filterType, filterYear]);

  useEffect(() => { load(); }, [load]);

  // Is the approval workflow switched on for Leave Applications? When on,
  // Submit routes through the generic engine and approvals happen in the
  // "My Approvals" inbox rather than inline here.
  const [workflowOn, setWorkflowOn] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/approvals/enabled/LeaveApplication`, { headers: hdrs() })
      .then(r => setWorkflowOn(!!r.data?.enabled))
      .catch(() => {});
  }, []);

  const sendForApproval = async (id) => {
    try {
      const { data } = await axios.post(`${API}/api/approvals/send`, { documentType: 'LeaveApplication', documentId: id }, { headers: hdrs() });
      setSuccess(data.autoApproved ? 'Approved' : 'Sent for approval');
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to send for approval'); }
  };

  const action = async (id, endpoint, body = {}) => {
    try {
      await axios.put(`${API}/api/leave/applications/${id}/${endpoint}`, body, { headers: hdrs() });
      setSuccess(`${endpoint.charAt(0).toUpperCase() + endpoint.slice(1)} successful`);
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) { setError(err.response?.data?.error || `${endpoint} failed`); }
  };

  const handleReject = async () => {
    await action(rejectDlg, 'reject', { rejectionReason: rejectReason });
    setRejectDlg(null); setRejectReason('');
  };

  const handleRecall = async () => {
    setRecalling(true);
    try {
      await axios.put(
        `${API}/api/leave/applications/${recallDlg._id}/recall`,
        { recallDate },
        { headers: hdrs() }
      );
      const restored = previewRecall(recallDlg, recallDate);
      setSuccess(`Recall posted — ${restored} day${restored !== 1 ? 's' : ''} restored to balance`);
      setTimeout(() => setSuccess(''), 4000);
      setRecallDlg(null);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Recall failed'); }
    finally { setRecalling(false); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const years = [];
  for (let y = new Date().getFullYear() + 1; y >= 2022; y--) years.push(y.toString());

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/hr/leave')}>Back</Button>
              <Assignment sx={{ color: '#1565c0' }} />
              <Typography variant="h5" fontWeight={700} color="#1565c0">Leave Applications</Typography>
              <Chip label={`${apps.length} records`} size="small" color="default" />
            </Box>
            <Button variant="contained" startIcon={<Add />}
              onClick={() => navigate('/hr/leave/applications/new')}
              sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
              New Application
            </Button>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Filter bar */}
          <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
            <TextField select label="Year" value={filterYear} size="small" sx={{ minWidth: 100 }}
              onChange={e => setFilterYear(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">All Years</MenuItem>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <TextField select label="Status" value={filterStatus} size="small" sx={{ minWidth: 160 }}
              onChange={e => setFilterStatus(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">All Statuses</MenuItem>
              {['Open', 'Pending Approval', 'Approved', 'Rejected', 'Cancelled', 'Recalled'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Leave Type" value={filterType} size="small" sx={{ minWidth: 180 }}
              onChange={e => setFilterType(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">All Types</MenuItem>
              {leaveTypes.map(lt => <MenuItem key={lt.code} value={lt.code}>{lt.name}</MenuItem>)}
            </TextField>
            <Button variant="outlined" size="small" onClick={() => { setFilterStatus(''); setFilterType(''); }}>
              Clear
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Ref No</strong></TableCell>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Leave Type</strong></TableCell>
                <TableCell><strong>Period</strong></TableCell>
                <TableCell align="center"><strong>Days</strong></TableCell>
                <TableCell><strong>Return</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center">Loading…</TableCell></TableRow>
              ) : apps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>No applications found.</Typography>
                  </TableCell>
                </TableRow>
              ) : apps.map(a => (
                <TableRow key={a._id} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" fontWeight={700}>
                      {a.applicationNo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{a.employeeName}</Typography>
                    <Typography variant="caption" color="text.secondary">{a.employeeNo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{a.leaveTypeName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmt(a.startDate)} – {fmt(a.endDate)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={a.leaveDays} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmt(a.returnDate)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={a.status} size="small" color={STATUS_COLOR[a.status] || 'default'} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {a.status === 'Open' && (
                      <>
                        <Tooltip title="Edit Application">
                          <IconButton size="small" color="primary" onClick={() => openEdit(a)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Submit for Approval">
                          <IconButton size="small" color="info"
                            onClick={() => workflowOn ? sendForApproval(a._id) : action(a._id, 'submit')}>
                            <Send fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {/* Inline approve/reject only when the workflow is OFF; otherwise
                        approvals are done in the My Approvals inbox by the routed approver. */}
                    {!workflowOn && ['Open', 'Pending Approval'].includes(a.status) && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton size="small" color="success" onClick={() => action(a._id, 'approve')}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton size="small" color="error" onClick={() => { setRejectDlg(a._id); setRejectReason(''); }}>
                            <ThumbDown fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" color="default" onClick={() => action(a._id, 'cancel')}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {a.status === 'Approved' && (
                      <Tooltip title="Recall Employee">
                        <IconButton size="small" sx={{ color: '#6a1b9a' }}
                          onClick={() => { setRecallDlg(a); setRecallDate(TODAY); }}>
                          <Undo fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      {discardDialog}
      <Dialog open={!!editDlg} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Edit fontSize="small" color="primary" />
            <Typography fontWeight={700}>Edit Leave Application</Typography>
            {editDlg && (
              <Chip label={editDlg.applicationNo} size="small" variant="outlined" sx={{ ml: 0.5, fontFamily: 'monospace' }} />
            )}
          </Box>
          {editDlg && (
            <Typography variant="caption" color="text.secondary">
              {editDlg.employeeName} · {editDlg.employeeNo}
            </Typography>
          )}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {editDlg && (() => {
            const isFixed = !!(editLT && editLT.maxDaysPerApplication > 0);
            const days = isFixed
              ? calDays(editForm.startDate, editForm.endDate)
              : workingDays(editForm.startDate, editForm.endDate);
            const rDate = nextWorkDay(editForm.endDate);
            const eligibleTypes = leaveTypes.filter(lt =>
              lt.isActive !== false &&
              (lt.gender === 'All' || lt.gender === employees.find(e => e.employeeNo === editDlg.employeeNo)?.gender)
            );
            return (
              <Grid container spacing={2}>
                {/* Leave type */}
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Leave Type *"
                    name="leaveTypeCode" value={editForm.leaveTypeCode}
                    onChange={handleEditField}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                    {eligibleTypes.map(lt => (
                      <MenuItem key={lt.code} value={lt.code}>
                        {lt.name}
                        {lt.daysPerYear !== null
                          ? <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>({lt.daysPerYear}d/yr)</span>
                          : <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>(unlimited)</span>}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Leave type info + balance */}
                {editLT && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {editLT.isPaid !== false ? '✓ Paid' : '✗ Unpaid'}
                        {editLT.requiresReliever && (
                          <span style={{ color: '#e65100', fontWeight: 700 }}> · Reliever required</span>
                        )}
                        {editLT.maxDaysPerApplication > 0 && ` · Max ${editLT.maxDaysPerApplication} days`}
                      </Typography>
                      {editLT.daysPerYear !== null && (
                        <Typography variant="body2" mt={0.3}>
                          Available balance:{' '}
                          <strong>{editBal === null ? '…' : `${editBal} days`}</strong>
                          {days > 0 && editBal !== null && (
                            <span style={{ color: editBal >= days ? '#2e7d32' : '#c62828', fontSize: 11, marginLeft: 6 }}>
                              {editBal >= days ? `✓ ok (${days} requested)` : `✗ insufficient (${days} requested)`}
                            </span>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Dates &*&*/}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Start Date *" type="date"
                    name="startDate" value={editForm.startDate}
                    onChange={handleEditField} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="End Date *" type="date"
                    name="endDate" value={editForm.endDate}
                    onChange={handleEditField} InputLabelProps={{ shrink: true }}
                    inputProps={{ min: editForm.startDate }}
                    helperText={isFixed ? `Auto-filled · ${editLT.maxDaysPerApplication} calendar days` : ''}
                    sx={isFixed ? { '& .MuiInputBase-input': { color: '#1565c0' } } : {}} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {isFixed ? 'Calendar days' : 'Working days'}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}
                      color={days > 0 ? '#1565c0' : 'text.disabled'} lineHeight={1.2}>
                      {days > 0 ? days : '—'}
                    </Typography>
                    {rDate && days > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Return: {new Date(rDate).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Reliever */}
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small"
                    label={editLT?.requiresReliever ? 'Reliever *' : 'Reliever'}
                    name="relieverNo" value={editForm.relieverNo}
                    onChange={handleEditField}
                    error={!!(editLT?.requiresReliever && !editForm.relieverNo)}
                    helperText={editLT?.requiresReliever && !editForm.relieverNo
                      ? 'Required — this leave type mandates a reliever'
                      : editLT?.requiresReliever ? 'Reliever required by leave type setup' : ''}
                    FormHelperTextProps={{ sx: { color: editLT?.requiresReliever && !editForm.relieverNo ? 'error.main' : 'warning.main' } }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                    <MenuItem value=""><em>— None —</em></MenuItem>
                    {employees
                      .filter(e => e.employeeNo !== editDlg.employeeNo)
                      .map(e => (
                        <MenuItem key={e.employeeNo} value={e.employeeNo}>
                          {e.firstName} {e.lastName} ({e.employeeNo})
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>

                {/* Reason */}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Reason / Remarks"
                    name="reason" value={editForm.reason}
                    onChange={handleEditField} multiline rows={2}
                    InputLabelProps={{ shrink: true }} />
                </Grid>

                {editErr && (
                  <Grid item xs={12}>
                    <Alert severity="error">{editErr}</Alert>
                  </Grid>
                )}

                {/* Supporting documents (sick sheets, letters…) */}
                <Grid item xs={12}>
                  <AttachmentsPanel refType="LeaveApplication" refId={editDlg.applicationNo} />
                </Grid>
              </Grid>
            );
          })()}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={attemptClose} size="small">Cancel</Button>
          <Button variant="contained" size="small" onClick={handleEditSave} disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={14} /> : <Edit fontSize="small" />}>
            {editSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectDlg} onClose={() => setRejectDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent dividers>
          <TextField label="Reason for rejection" value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            fullWidth size="small" multiline rows={3} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Recall dialog */}
      <Dialog open={!!recallDlg} onClose={() => setRecallDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Undo sx={{ color: '#6a1b9a' }} /> Recall Employee from Leave
        </DialogTitle>
        <DialogContent dividers>
          {recallDlg && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 1 }}>
                  <Typography variant="body2" fontWeight={700}>{recallDlg.employeeName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {recallDlg.leaveTypeName} · {fmt(recallDlg.startDate)} – {fmt(recallDlg.endDate)} · {recallDlg.leaveDays} days
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Recall Date *" type="date" value={recallDate}
                  onChange={e => setRecallDate(e.target.value)}
                  fullWidth size="small" InputLabelProps={{ shrink: true }}
                  helperText="The date the employee resumes duty" />
              </Grid>
              {recallDate && (
                <Grid item xs={12}>
                  {(() => {
                    const restored = previewRecall(recallDlg, recallDate);
                    return (
                      <Box sx={{
                        p: 1.5, borderRadius: 1, border: '1px solid',
                        borderColor: restored > 0 ? '#6a1b9a44' : 'divider',
                        bgcolor: restored > 0 ? '#f3e5f5' : 'grey.50'
                      }}>
                        <Typography variant="caption" color="text.secondary" display="block">Days to be restored</Typography>
                        <Typography variant="h5" fontWeight={800} color={restored > 0 ? '#6a1b9a' : 'text.disabled'} lineHeight={1.2}>
                          {restored}
                        </Typography>
                        {restored === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Leave period has ended — no days to restore
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecallDlg(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRecall} disabled={recalling || !recallDate}
            sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}>
            {recalling ? 'Posting…' : 'Confirm Recall'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveApplicationList;
